/**
 * Event Recap (Report 2) — one event, top to bottom as on Jarea's form:
 * header · assumptions (editable by event_editor) · performance (As saved | Live | Δ)
 * · team stats · definitions. Saving the assumptions also freezes a snapshot of
 * every number (R2-05); the Live column recomputes on each data refresh (R2-06).
 * Exports: Excel and PDF, both client-side and loaded on demand (R2-10).
 */
import { AlertCircle, AlertTriangle, ArrowLeft, Check, FileDown, FileSpreadsheet, Pencil, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Link, useParams } from "react-router-dom";

import { useAuth } from "../../auth/AuthContext";
import { DefinitionsPanel } from "../../events/DefinitionsPanel";
import { EventsNav } from "../../events/EventsNav";
import { RECAP_DEFINITIONS } from "../../events/definitions";
import { money, pct, stampLabel } from "../../events/format";
import { ASSUMPTION_FIELDS, PERF_ROWS, PERF_SECTIONS, fmtDelta, fmtValue, perfValue } from "../../events/recapFields";
import { downloadXlsx } from "../../events/xlsx";
import { fetchRecap, patchRecap, saveRecapAssumptions } from "../../lib/eventReporting";
import type { RecapAssumptionsIn, RecapDetail } from "../../lib/eventReporting";

const inputCls = "px-3 py-1.5 rounded-md border hairline text-[13.5px] focus-ring w-full";
const inputStyle = { background: "var(--bg)", color: "var(--ink)" };
const longDate = (d: string) =>
  new Date(`${d}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });

type FormState = Record<keyof RecapAssumptionsIn, string>;

function toForm(a: Partial<RecapAssumptionsIn> | null): FormState {
  const out = {} as FormState;
  for (const f of ASSUMPTION_FIELDS) {
    const v = a?.[f.key];
    out[f.key] = v === null || v === undefined ? "" : String(v);
  }
  return out;
}

function fromForm(s: FormState): { body?: RecapAssumptionsIn; error?: string } {
  const body = {} as Record<string, string | number | null>;
  for (const f of ASSUMPTION_FIELDS) {
    const raw = s[f.key].trim();
    if (raw === "") { body[f.key] = null; continue; }
    if (f.kind === "int" || f.kind === "money") {
      const n = Number(raw.replace(/[$,]/g, ""));
      if (Number.isNaN(n) || n < 0 || (f.kind === "int" && !Number.isInteger(n))) {
        return { error: `${f.label} must be ${f.kind === "int" ? "a whole number" : "an amount"} of 0 or more.` };
      }
      body[f.key] = n;
    } else {
      body[f.key] = raw;
    }
  }
  return { body: body as unknown as RecapAssumptionsIn };
}

export function EventRecap() {
  const { id = "" } = useParams();
  const { user } = useAuth();
  const canEdit = !!user?.has_event_editor;
  const [d, setD] = useState<RecapDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(toForm(null));
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [editing, setEditing] = useState(false);
  const [exporting, setExporting] = useState<string | null>(null);

  useEffect(() => {
    setD(null);
    fetchRecap(id)
      .then((r) => { setD(r); setForm(toForm(r.assumptions)); })
      .catch((e: Error) => setError(e.message));
  }, [id]);

  const r = d?.recap;
  const saved = d?.latest_snapshot?.rollup ?? null;
  const pending = !!r && !r.computed_at;
  const dirty = useMemo(() => !!d && JSON.stringify(form) !== JSON.stringify(toForm(d.assumptions)), [form, d]);

  async function save(e: FormEvent) {
    e.preventDefault();
    setSaveMsg(null);
    const { body, error: err } = fromForm(form);
    if (err || !body) return setSaveMsg({ ok: false, text: err ?? "Check the fields." });
    setSaving(true);
    try {
      const next = await saveRecapAssumptions(id, body);
      setD(next);
      setForm(toForm(next.assumptions));
      setSaveMsg({ ok: true, text: `Saved. The numbers as of now are frozen as save #${next.snapshot_count}.` });
    } catch (e2) {
      setSaveMsg({ ok: false, text: (e2 as Error).message });
    } finally {
      setSaving(false);
    }
  }

  async function exportXlsx() {
    if (!d || !r) return;
    setExporting("xlsx");
    try {
      const XFMT = { int: "#,##0", money: "$#,##0.00", pct: "0.0%", hours: "#,##0.00" } as const;
      await downloadXlsx(`recap_${r.display_name.replace(/[^\w-]+/g, "_")}.xlsx`, [
        {
          sheet: "Recap",
          title: [r.display_name, `${r.subsource} · ${longDate(r.start_date)} to ${longDate(r.end_date)}`,
            `Data as of ${stampLabel(r.computed_at)}${d.latest_snapshot ? ` · last saved ${stampLabel(d.latest_snapshot.saved_at)}` : ""}`],
          header: ["Metric", "As saved", "Live"],
          rows: PERF_ROWS.map((p) => [p.label, perfValue(saved, p.key), perfValue(r, p.key)]),
          cellFormat: (ri) => XFMT[PERF_ROWS[ri].fmt],
        },
        {
          sheet: "Assumptions",
          header: ["Field", "Value"],
          rows: ASSUMPTION_FIELDS.map((f) => [f.label, d.assumptions?.[f.key] ?? null]),
          cellFormat: (ri) => (ASSUMPTION_FIELDS[ri].kind === "money" ? XFMT.money : undefined),
        },
        {
          sheet: "Team stats",
          header: ["Promoter", "Hours", "Leads", "Sets", "Demoed", "Sales #", "Sales $"],
          rows: d.promoters.map((p) => [p.promoter_name, p.hours, p.leads, p.sets, p.demos, p.sales_count, p.sales_amount]),
          money: [6],
        },
      ]);
    } finally {
      setExporting(null);
    }
  }

  async function exportPdf() {
    if (!d) return;
    setExporting("pdf");
    try {
      const { downloadRecapPdf } = await import("../../events/RecapPdf");
      await downloadRecapPdf(d);
    } finally {
      setExporting(null);
    }
  }

  if (error) {
    return (
      <div className="p-8">
        <EventsNav />
        <div className="rounded-md p-4 font-mono text-[13px] flex items-start gap-2"
          style={{ background: "var(--oxblood-bg)", color: "var(--oxblood)", border: "1px solid var(--oxblood)" }}>
          <AlertCircle size={14} className="mt-0.5 shrink-0" /> <span>Failed to load the recap: {error}</span>
        </div>
      </div>
    );
  }
  if (!d || !r) {
    return <div className="p-8 font-serif italic text-[14px]" style={{ color: "var(--ink-3)" }}>Loading recap…</div>;
  }

  const anticipated = d.assumptions?.anticipated_net_revenue ?? null;

  return (
    <div className="p-8 view-in overflow-y-auto h-full scroll-quiet">
      <header className="mb-4 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <Link to="/events" className="inline-flex items-center gap-1 text-[13px] mb-2 focus-ring rounded-sm" style={{ color: "var(--ink-3)" }}>
            <ArrowLeft size={13} /> Event Recaps
          </Link>
          <h1 className="font-serif text-[28px] leading-tight" style={{ color: "var(--ink)" }}>{r.display_name}</h1>
          <p className="text-[14px] mt-1" style={{ color: "var(--ink-3)" }}>
            {r.subsource} · {longDate(r.start_date)}{r.end_date !== r.start_date ? ` to ${longDate(r.end_date)}` : ""}
            {r.attribution_end !== r.end_date ? ` · late leads through ${longDate(r.attribution_end)}` : ""}
            {canEdit && !editing && (
              <button type="button" onClick={() => setEditing(true)} className="ml-2 inline-flex items-center gap-1 underline focus-ring rounded-sm">
                <Pencil size={12} /> Edit details
              </button>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={exportXlsx} disabled={!!exporting}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border hairline font-serif text-[14px] t-base focus-ring disabled:opacity-40"
            style={{ color: "var(--ink)" }}>
            <FileSpreadsheet size={14} /> {exporting === "xlsx" ? "Exporting…" : "Excel"}
          </button>
          <button type="button" onClick={exportPdf} disabled={!!exporting}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md font-serif text-[14px] t-base focus-ring disabled:opacity-40"
            style={{ background: "var(--ink)", color: "var(--bg)" }}>
            <FileDown size={14} /> {exporting === "pdf" ? "Preparing PDF…" : "PDF"}
          </button>
        </div>
      </header>

      <EventsNav />

      {editing && <EditDetails d={d} onDone={(next) => { if (next) setD(next); setEditing(false); }} id={id} />}

      <div className="flex flex-col gap-2 mb-5 text-[13px]">
        {pending ? (
          <Banner tone="brass" icon={<AlertTriangle size={14} />}>
            Pending first refresh: this event's numbers fill in at the next data refresh (5–9 AM or 4 PM Central).
          </Banner>
        ) : (
          <div style={{ color: "var(--ink-3)" }}>
            Data as of {stampLabel(r.computed_at)}
            {d.latest_snapshot && <> · last saved {stampLabel(d.latest_snapshot.saved_at)} by {d.latest_snapshot.saved_by_email ?? d.latest_snapshot.saved_by} ({d.snapshot_count} save{d.snapshot_count === 1 ? "" : "s"})</>}
          </div>
        )}
        {(r.unmatched_hours ?? 0) > 0 && (
          <Banner tone="brass" icon={<AlertTriangle size={14} />}>
            {fmtValue(r.unmatched_hours, "hours")} hours are logged under {r.unmatched_names?.join(", ")}, which matches no
            LeadPerfection promoter. Fix the name in LeadPerfection so those hours line up with the right person.
          </Banner>
        )}
        {r.rates_incomplete && !pending && (
          <Banner tone="slate" icon={<AlertCircle size={14} />}>
            Commission {pct(r.commission_pct)} and override {pct(r.override_pct)} are in effect for this event's date;
            anything "Not set" counts as $0 in the totals until it's entered on the Rates page.
          </Banner>
        )}
      </div>

      <div className="grid xl:grid-cols-[minmax(320px,1fr)_2fr] gap-4 mb-5 items-start">
        {/* Assumptions */}
        <section className="rounded-lg border hairline px-5 py-4" style={{ background: "var(--bg-elev)" }}>
          <div className="font-serif text-[15px] mb-3" style={{ color: "var(--ink)" }}>Assumptions</div>
          {canEdit ? (
            <form onSubmit={save} className="grid gap-3">
              {ASSUMPTION_FIELDS.map((f) => (
                <label key={f.key} className="block">
                  <div className="text-[10.5px] font-mono uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ink-3)" }}>{f.label}</div>
                  {f.kind === "long" ? (
                    <textarea rows={3} maxLength={4000} value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      className={inputCls} style={inputStyle} />
                  ) : (
                    <input value={form[f.key]} inputMode={f.kind === "text" ? undefined : "decimal"}
                      placeholder={f.kind === "money" ? "$" : undefined}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className={inputCls} style={inputStyle} />
                  )}
                </label>
              ))}
              <div className="flex items-center gap-3 flex-wrap">
                <button type="submit" disabled={saving || !dirty}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md font-serif text-[14px] t-base focus-ring disabled:opacity-40"
                  style={{ background: "var(--ink)", color: "var(--bg)" }}>
                  {saving ? "Saving…" : "Save recap"}
                </button>
                {saveMsg && <span className="text-[13px]" style={{ color: saveMsg.ok ? "var(--forest)" : "var(--oxblood)" }}>{saveMsg.text}</span>}
              </div>
              <p className="text-[12px]" style={{ color: "var(--ink-3)" }}>
                Saving also freezes every number on this page as it is right now.
              </p>
            </form>
          ) : (
            <dl className="grid gap-2 text-[13.5px]">
              {ASSUMPTION_FIELDS.map((f) => (
                <div key={f.key} className="grid grid-cols-[150px_1fr] gap-2">
                  <dt style={{ color: "var(--ink-3)" }}>{f.label}</dt>
                  <dd style={{ color: "var(--ink)" }}>
                    {d.assumptions?.[f.key] == null ? "–" : f.kind === "money" ? money(Number(d.assumptions[f.key]))
                      : f.kind === "int" ? fmtValue(Number(d.assumptions[f.key]), "int") : String(d.assumptions[f.key])}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </section>

        {/* Performance */}
        <section className="rounded-lg border hairline overflow-hidden" style={{ background: "var(--bg-elev)" }}>
          <div className="font-serif text-[15px] px-5 pt-4 pb-2" style={{ color: "var(--ink)" }}>Performance</div>
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-[10.5px] font-mono uppercase tracking-[0.14em] border-b hairline" style={{ color: "var(--ink-3)" }}>
                <th className="px-5 py-2 font-normal text-left">Metric</th>
                {saved && <th className="px-3 py-2 font-normal text-right">As saved</th>}
                <th className="px-3 py-2 font-normal text-right">Live</th>
                {saved && <th className="px-5 py-2 font-normal text-right">Change</th>}
              </tr>
            </thead>
            <tbody>
              {PERF_SECTIONS.map((s) => (
                <SectionRows key={s.title} title={s.title} cols={saved ? 4 : 2}>
                  {s.rows.map((p) => {
                    const live = perfValue(r, p.key), was = perfValue(saved, p.key);
                    return (
                      <tr key={p.key} className="border-b hairline last:border-0" style={{ color: "var(--ink)", fontWeight: p.total ? 600 : 400 }}>
                        <td className="px-5 py-1.5">{p.label}</td>
                        {saved && <td className="px-3 py-1.5 text-right font-mono" style={{ color: "var(--ink-2)" }}>{fmtValue(was, p.fmt)}</td>}
                        <td className="px-3 py-1.5 text-right font-mono">{pending ? "–" : fmtValue(live, p.fmt)}</td>
                        {saved && <td className="px-5 py-1.5 text-right font-mono" style={{ color: "var(--ink-3)" }}>{fmtDelta(live, was, p.fmt)}</td>}
                      </tr>
                    );
                  })}
                </SectionRows>
              ))}
              <SectionRows title="Anticipated vs actual" cols={saved ? 4 : 2}>
                <tr className="border-b hairline" style={{ color: "var(--ink)" }}>
                  <td className="px-5 py-1.5">Anticipated Net Revenue</td>
                  {saved && <td />}
                  <td className="px-3 py-1.5 text-right font-mono">{fmtValue(anticipated, "money")}</td>
                  {saved && <td />}
                </tr>
                <tr className="border-b hairline" style={{ color: "var(--ink)" }}>
                  <td className="px-5 py-1.5">Actual Net Revenue</td>
                  {saved && <td />}
                  <td className="px-3 py-1.5 text-right font-mono">{fmtValue(r.sales_amount, "money")}</td>
                  {saved && <td />}
                </tr>
                <tr style={{ color: "var(--ink)", fontWeight: 600 }}>
                  <td className="px-5 py-1.5">Actual − Anticipated</td>
                  {saved && <td />}
                  <td className="px-3 py-1.5 text-right font-mono">
                    {anticipated == null || r.sales_amount == null ? "–" : fmtDelta(r.sales_amount, anticipated, "money")}
                  </td>
                  {saved && <td />}
                </tr>
              </SectionRows>
            </tbody>
          </table>
        </section>
      </div>

      {/* Team stats */}
      <section className="rounded-lg border hairline overflow-hidden mb-5" style={{ background: "var(--bg-elev)" }}>
        <div className="font-serif text-[15px] px-5 pt-4 pb-2" style={{ color: "var(--ink)" }}>Team stats</div>
        {d.promoters.length === 0 ? (
          <div className="px-5 pb-4 font-serif italic text-[13.5px]" style={{ color: "var(--ink-3)" }}>
            {pending ? "Fills in at the next data refresh." : "No promoter activity recorded for this event."}
          </div>
        ) : (
          <div className="overflow-x-auto scroll-quiet">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-[10.5px] font-mono uppercase tracking-[0.14em] border-b hairline" style={{ color: "var(--ink-3)" }}>
                  {["Promoter", "Hours", "Leads", "Appts Set", "Demoed", "Sales #", "Sales $"].map((h, i) => (
                    <th key={h} className={`${i === 0 ? "px-5 text-left" : "px-3 text-right"} py-2 font-normal whitespace-nowrap`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {d.promoters.map((p) => (
                  <tr key={p.promoter_name} className="border-b hairline last:border-0" style={{ color: "var(--ink)" }}>
                    <td className="px-5 py-1.5 whitespace-nowrap">
                      {p.promoter_name}
                      {!p.matched_to_leads && (
                        <span className="ml-2 text-[11.5px] inline-flex items-center gap-1" style={{ color: "var(--brass)" }}>
                          <AlertTriangle size={12} /> no matching LP promoter
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono">{fmtValue(p.hours, "hours")}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{p.leads}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{p.sets}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{p.demos}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{p.sales_count}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{fmtValue(p.sales_amount, "money")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <DefinitionsPanel items={RECAP_DEFINITIONS}
        summary={`Commission ${pct(r.commission_pct)} · Override ${pct(r.override_pct)} · Demo pay ${r.demo_pay_rate == null ? "Not set" : money(r.demo_pay_rate)}`} />
    </div>
  );
}

function SectionRows({ title, cols, children }: { title: string; cols: number; children: ReactNode }) {
  return (
    <>
      <tr>
        <td colSpan={cols} className="px-5 pt-3 pb-1 text-[10.5px] font-mono uppercase tracking-[0.14em]" style={{ color: "var(--ink-3)" }}>
          {title}
        </td>
      </tr>
      {children}
    </>
  );
}

function Banner({ tone, icon, children }: { tone: "brass" | "slate"; icon: ReactNode; children: ReactNode }) {
  return (
    <div className="rounded-md px-3 py-2 flex items-start gap-2" style={{ background: `var(--${tone}-bg)`, color: `var(--${tone === "brass" ? "ink" : "ink-2"})` }}>
      <span className="mt-0.5 shrink-0" style={{ color: `var(--${tone})` }}>{icon}</span>
      <span>{children}</span>
    </div>
  );
}

function EditDetails({ d, id, onDone }: { d: RecapDetail; id: string; onDone: (next: RecapDetail | null) => void }) {
  const r = d.recap;
  const [name, setName] = useState(r.display_name);
  const [start, setStart] = useState(r.start_date);
  const [end, setEnd] = useState(r.end_date);
  const [attr, setAttr] = useState(r.attribution_end !== r.end_date ? r.attribution_end : "");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      onDone(await patchRecap(id, { display_name: name, start_date: start, end_date: end, attribution_end: attr || null }));
    } catch (e2) {
      setErr((e2 as Error).message);
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="rounded-lg border hairline px-5 py-4 mb-5 grid gap-3 max-w-3xl" style={{ background: "var(--bg-elev)" }}>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        {([["Display name", name, setName, "text"], ["Start", start, setStart, "date"], ["End", end, setEnd, "date"], ["Late leads through", attr, setAttr, "date"]] as const).map(
          ([label, value, set, type]) => (
            <label key={label} className="block">
              <div className="text-[10.5px] font-mono uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ink-3)" }}>{label}</div>
              <input type={type} required={label !== "Late leads through"} value={value} onChange={(e) => set(e.target.value)} className={inputCls} style={inputStyle} />
            </label>
          ),
        )}
      </div>
      {err && <div className="text-[13px]" style={{ color: "var(--oxblood)" }}>{err}</div>}
      <div className="flex gap-2">
        <button type="submit" disabled={busy} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md font-serif text-[13.5px] focus-ring"
          style={{ background: "var(--ink)", color: "var(--bg)" }}><Check size={13} /> Save details</button>
        <button type="button" onClick={() => onDone(null)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border hairline text-[13.5px] focus-ring"
          style={{ color: "var(--ink)" }}><X size={13} /> Cancel</button>
      </div>
    </form>
  );
}
